import React from 'react';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';


import usePageTitle from '../../hooks/UsePageTitle';
const SchoolOperations = () => {
  usePageTitle('School Operations & Development');

  return (
    <div className="page-container">
      <div className="grid">
        <div className="col-12">
          <h1 className="text-center">School Management Services – School Operations & Development</h1>
          <Divider />
        </div>

        {/* Introduction Section */}
        <div className="col-12 md:col-6">
          <img 
            src='/images/school (4).jpg' 
            alt="School Operations & Development" 
            className="w-full border-round shadow-2 mb-4" 
            style={{ maxHeight: '400px', objectFit: 'cover' }}
          />
        </div>
        <div className="col-12 md:col-6">
          <Card className="h-full">
            <p className="text-lg line-height-3">
              Once a school is established, the true challenge begins—running it efficiently while upholding 
              the highest standards of education, governance, compliance, and student development.
            </p>
            <p className="text-lg line-height-3 mt-3">
              At SnP Finvisors, we specialize in optimizing and managing the day-to-day operations of educational 
              institutions to ensure academic excellence, financial sustainability, and stakeholder satisfaction.
            </p>
            <p className="text-lg line-height-3 mt-3 font-semibold">
              We work with both new and existing schools to professionalize their operations, streamline their 
              management systems, and design holistic development programs that help institutions thrive in a 
              competitive education landscape.
            </p>
          </Card>
        </div>

        {/* Key Pillars Section */}
        <div className="col-12 mt-5">
          <h2>Key Pillars of School Operations & Development</h2>
          <Divider />
          
          <div className="grid">
            {/* Pillar 1 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="1. Academic Performance Management" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Implementation of structured academic calendars, lesson plans, and teaching strategies</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Integration of modern pedagogical tools like Bloom's taxonomy, flipped classrooms, and differentiated instruction</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Designing academic audits, performance reviews, and teacher training modules to ensure delivery quality</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 2 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="2. Faculty Development & HR Advisory" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Ongoing teacher training programs (pedagogy, classroom management, NEP integration, etc.)</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>HR frameworks including recruitment, performance appraisals, goal-setting, and retention strategies</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Establishment of grievance redressal mechanisms and positive workplace culture initiatives</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 3 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="3. Administrative System Implementation" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Setup or enhancement of ERP platforms for efficient handling of admissions, attendance, fees, academic tracking, and parent communication</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Process automation for transport, canteen, inventory, and hostel management (if applicable)</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Development of SOPs for front-desk operations, admissions, and event coordination</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 4 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="4. Parent & Community Engagement" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Designing structured Parent-Teacher Meeting (PTM) frameworks for feedback and collaboration</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Building communication protocols across various platforms (email, SMS, app notifications, etc.)</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Organizing school events, exhibitions, and social programs to build trust and visibility</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 5 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="5. Budgeting & Financial Management" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Preparation of school-wide annual budgets and tracking of revenue, fees, grants, and expenses</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Implementation of controls to avoid misuse of funds and ensure optimal resource allocation</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advisory on fund utilization, cash flow monitoring, and forecasting for future development</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 6 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="6. Regulatory Compliance Monitoring" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Ensuring continued adherence to board-specific norms (CBSE, ICSE, IB, etc.)</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Management of compliance documents, safety audits, inspections, and filing of regulatory reports</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Advisory on annual returns, affiliation renewals, and labor law requirements</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 7 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="7. Student Development & Co-Scholastic Planning" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Designing programs that enhance student learning beyond academics: sports, arts, leadership, and values education</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Implementing structured career guidance cells, mental health support systems, and talent development pathways</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Monitoring attendance, behavior, and performance through integrated dashboards</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 8 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="8. Infrastructure Maintenance & Vendor Management" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Development of maintenance schedules for buildings, furniture, labs, IT infrastructure, and security systems</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Evaluation and onboarding of reliable vendors for transport, cafeteria, uniforms, books, etc.</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Implementation of AMC contracts and quality control processes</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 9 */}
            <div className="col-12 md:col-6 lg:col-4">
              <Card title="9. Performance Reporting & Analytics" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Designing school-wide KPIs and dashboards for management review</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Reporting on academic performance, admissions, finances, HR, and infrastructure health</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Conducting internal audits to identify gaps and recommend timely interventions</span>
                  </li>
                </ul>
              </Card>
            </div>

            {/* Pillar 10 */}
            <div className="col-12">
              <Card title="10. Strategic Planning & Growth Advisory" className="h-full">
                <ul className="list-none p-0 m-0">
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Long-term planning support for expansion, diversification (e.g., pre-primary, junior college), or affiliations</span>
                  </li>
                  <li className="mb-2 flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Branding and reputation management through marketing initiatives and academic excellence awards</span>
                  </li>
                  <li className="flex align-items-start">
                    <i className="pi pi-check-circle text-primary mr-2 mt-1"></i>
                    <span>Fundraising and donor engagement strategies for trust-run schools or NGOs</span>
                  </li>
                </ul>
              </Card>
            </div>
          </div>
        </div>

        {/* Why Partner Section */}
        <div className="col-12 mt-5">
          <h2>Why Partner with SnP Finvisors?</h2>
          <Divider />
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Education-Sector Expertise</h3>
                <p>
                  Our team brings real-world experience from working with leading schools and educational institutions.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Operational Excellence</h3>
                <p>
                  We instill structure, accountability, and performance-driven culture in every school.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Technology-Driven</h3>
                <p>
                  We help schools adopt the right mix of ERP systems, learning management tools, and analytics platforms.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Continuous Improvement</h3>
                <p>
                  Our engagement doesn't stop with setup—we implement ongoing reviews, audits, and training for sustained improvement.
                </p>
              </Card>
            </div>
            <div className="col-12 md:col-6 lg:col-4">
              <Card className="h-full">
                <h3 className="mt-0">Custom Solutions</h3>
                <p>
                  No two schools are alike. We tailor our operational frameworks to the unique ethos, vision, and resources of each institution.
                </p>
              </Card>
            </div>
          </div>
        </div>

        {/* Ideal For Section */}
        <div className="col-12 mt-5">
          <h2>Ideal for:</h2>
          <Divider />
          <div className="grid">
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-star text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Newly established schools requiring professional operations setup</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-chart-line text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Existing schools aiming to upgrade systems, boost performance, or scale operations</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-sitemap text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Trusts or NGOs managing multiple school branches</span>
              </div>
            </div>
            <div className="col-12 md:col-6 lg:col-3">
              <div className="flex align-items-center mb-3">
                <i className="pi pi-shield text-primary mr-3 text-xl"></i>
                <span className="font-semibold">Schools preparing for audits, board inspections, or performance benchmarking</span>
              </div>
            </div>
          </div>
        </div>

        {/* Closing Section */}
        <div className="col-12 mt-5">
          <div className="flex flex-column md:flex-row gap-5">
            <img 
              src='/images/school (3).jpg' 
              alt="School Management Solutions" 
              className="w-full md:w-6 border-round shadow-2" 
              style={{ maxHeight: '350px', objectFit: 'cover' }}
            />
            <div className="flex-1">
              <h2>Empower Your Institution with Smart Management</h2>
              <Divider />
              <p className="text-xl line-height-3">
                SnP Finvisors helps your school move beyond survival mode and into a phase of structured growth and 
                lasting impact. With our deep sector expertise, strategic insight, and hands-on support, we turn 
                everyday school operations into engines of excellence.
              </p>
              <p className="text-xl line-height-3 mt-3 font-semibold">
                Contact us to know how we can transform your school's operations for a smarter, stronger future.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SchoolOperations;